import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LogOut, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Card, CardAction, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import type { DeviceSession, SeatOverview } from '../../../common/hooks/use-devices-api.hook';
import { DESTRUCTIVE, DeviceCard, deviceTitle } from './DeviceCard';

interface Props {
  load: () => Promise<SeatOverview>;
  revoke: (id: string) => Promise<unknown>;
  rename?: (id: string, label: string) => Promise<unknown>;
  revokeOthers?: () => Promise<{ count: number }>;
  /** Signing out the current device ends this browser's session: the caller logs out. */
  onSignedOutHere?: () => void;
  compact?: boolean;
}


/** Seats in use and every signed-in device, with sign-out. Shared by the vendor page and support. */
export function DeviceSessionsView({ load, revoke, rename, revokeOthers, onSignedOutHere, compact }: Props) {
  const { t } = useTranslation();
  const [data, setData] = useState<SeatOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<DeviceSession | 'others' | null>(null);
  const [busy, setBusy] = useState(false);

  // API hooks (and `t`) can be new functions on any render; fetching must not
  // follow them, or every load re-renders and triggers the next load.
  const loadRef = useRef(load);
  loadRef.current = load;
  const tRef = useRef(t);
  tRef.current = t;

  const refresh = useCallback(async () => {
    try {
      setError(null);
      setData(await loadRef.current());
    } catch (e: any) {
      setError(e?.message || tRef.current('SEATS.PAGE.LOAD_FAILED'));
    }
  }, []);

  useEffect(() => {
    refresh();
    // Keep "active now" honest while the page is open.
    const id = setInterval(refresh, 60_000);
    return () => clearInterval(id);
  }, [refresh]);

  const confirm = async () => {
    if (!confirming) return;
    setBusy(true);
    try {
      if (confirming === 'others') {
        const { count } = await revokeOthers!();
        toast.success(t('SEATS.PAGE.SIGNED_OUT_OTHERS', { count }));
      } else {
        await revoke(confirming.id);
        if (confirming.current) {
          setConfirming(null);
          onSignedOutHere?.();
          return;
        }
        toast.success(t('SEATS.PAGE.SIGNED_OUT_ONE', { device: deviceTitle(confirming, t) }));
      }
      setConfirming(null);
      await refresh();
    } catch (e: any) {
      toast.error(e?.message || t('SEATS.PAGE.ACTION_FAILED'));
    } finally {
      setBusy(false);
    }
  };

  const onRename = rename
    ? async (s: DeviceSession, label: string) => {
        try {
          await rename(s.id, label);
          await refresh();
        } catch (e: any) {
          toast.error(e?.message || t('SEATS.PAGE.ACTION_FAILED'));
        }
      }
    : undefined;

  const sessions = data?.sessions ?? [];
  const others = sessions.filter((s) => !s.current).length;

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <CardHeader>
          <CardTitle>{t('SEATS.PAGE.DEVICES_TITLE')}</CardTitle>
          <CardDescription>
            {t('SEATS.PAGE.DEVICES_DESC', { minutes: data?.idleMinutes ?? 30 })}
          </CardDescription>
          <CardAction className="flex gap-2">
            <Button variant="ghost" size="icon-sm" onClick={refresh} aria-label={t('SEATS.PAGE.REFRESH')}>
              <RefreshCw />
            </Button>
            {revokeOthers && others > 0 && (
              <Button variant="outline" size="sm" onClick={() => setConfirming('others')}>
                <LogOut data-icon="inline-start" />
                {t('SEATS.PAGE.SIGN_OUT_OTHERS')}
              </Button>
            )}
          </CardAction>
        </CardHeader>
      </Card>

      {error && <p className="text-sm text-destructive">{error}</p>}

      {!data && !error ? (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-48" />)}
        </div>
      ) : sessions.length ? (
        <div className={compact ? 'grid gap-3 md:grid-cols-2' : 'grid gap-4 md:grid-cols-2 xl:grid-cols-3'}>
          {sessions.map((s) => (
            <DeviceCard key={s.id} session={s} onRevoke={setConfirming} onRename={onRename} busy={busy} />
          ))}
        </div>
      ) : data ? (
        <p className="py-10 text-center text-sm text-muted-foreground">{t('SEATS.PAGE.NONE')}</p>
      ) : null}

      <AlertDialog open={!!confirming} onOpenChange={(o) => !o && !busy && setConfirming(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirming === 'others'
                ? t('SEATS.PAGE.CONFIRM_OTHERS_TITLE', { count: others })
                : confirming?.current
                  ? t('SEATS.PAGE.CONFIRM_HERE_TITLE')
                  : t('SEATS.PAGE.CONFIRM_ONE_TITLE', { device: confirming ? deviceTitle(confirming, t) : '' })}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirming === 'others'
                ? t('SEATS.PAGE.CONFIRM_OTHERS_BODY')
                : confirming?.current
                  ? t('SEATS.PAGE.CONFIRM_HERE_BODY')
                  : t('SEATS.PAGE.CONFIRM_ONE_BODY', { email: confirming ? confirming.email : '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>{t('SEATS.DEVICE.CANCEL')}</AlertDialogCancel>
            <AlertDialogAction variant="outline" className={DESTRUCTIVE} disabled={busy} onClick={(e) => { e.preventDefault(); confirm(); }}>
              {t('SEATS.DEVICE.SIGN_OUT')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
